import React, { type FC, useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { at, isNil } from 'lodash-es';
import { VList, type VListHandle } from 'virtua';

import type { AssetsAssetTranscriptionPropertiesSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type {
  SpeakerLabels,
  SpeakerPart,
} from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { useVirtualizedScrollToIds } from '@iconik-io/shared-lib/src/react/hooks/use_virtualized_scroll_to_ids';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';
import { LANGUAGES_WITHOUT_SPACE } from '@iconik-io/shared-lib/src/utils/constants/files';
import { Icon } from '@iconik-io/uad';

import { TriggerMenu } from '../../../components/trigger_menu/trigger_menu';
import {
  TRANSCRIPTIONS_CHANGE_SEGMENTS_SPEAKER,
  TRANSCRIPTIONS_EDIT_SPEAKERS,
} from '../../../constants/modals';
import type { OpenModalPayload } from '../../../ducks/modals_manager/slice';
import type { MenuItem } from '../../../ui/menus/menu/types';
import { selectSpeakerMenuItems } from '../selectors';

import mixins from '../../../styles/core/mixins.module.css';
import styles from './speaker_mode.module.css';

type SpeakerButtonMenuProps = { name: string };

const SpeakerButtonMenu: FC<SpeakerButtonMenuProps> = (props) => (
  <button className={classNames(styles.speaker_button, mixins.reset_button)}>
    <span className={styles.speaker_label}>{props.name}</span>
    <Icon name="chevron_small_isolated_s" size="xs" />
  </button>
);

const speakerColorClasses = [
  styles.speaker_color_0,
  styles.speaker_color_1,
  styles.speaker_color_2,
  styles.speaker_color_3,
];

type Props = {
  openModal: (data: OpenModalPayload) => void;
  speakerLabels: SpeakerLabels;
  speakerParts: SpeakerPart[];
  segments?: Entities<SegmentEntityWithIdNormalized>;
  canEdit: boolean;
  selectedWordIds: string[];
  highlightedTextWordIds: string[];
  markedWordIds: string[];
  currentMatchWordId?: string;
  transcriptionProperties:
    | AssetsAssetTranscriptionPropertiesSchemaWithIdNullable
    | undefined;
};

export const Words: FC<Props> = ({
  canEdit,
  highlightedTextWordIds,
  markedWordIds,
  currentMatchWordId,
  openModal,
  selectedWordIds,
  speakerLabels,
  speakerParts,
  segments,
  transcriptionProperties,
}) => {
  const selectedWordIdsSet = useMemo(
    () => new Set(selectedWordIds),
    [selectedWordIds],
  );
  const highlightedTextWordIdsSet = useMemo(
    () => new Set(highlightedTextWordIds),
    [highlightedTextWordIds],
  );
  const markedWordIdsSet = useMemo(
    () => new Set(markedWordIds),
    [markedWordIds],
  );

  const vListRef = useRef<VListHandle>(null);
  const partIds = useMemo(
    () => speakerParts.map((_, index) => String(index)),
    [speakerParts],
  );
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: partIds,
  });

  useEffect(() => {
    if (!currentMatchWordId) {
      return;
    }

    const segmentId = currentMatchWordId.split('_')[1];
    const partIndex = speakerParts.findIndex((part) =>
      part.ids.includes(segmentId),
    );
    if (partIndex < 0) {
      return;
    }

    setScrollToIds([String(partIndex)]);

    const timeoutId = setTimeout(() => {
      document
        .getElementById(currentMatchWordId)
        ?.scrollIntoView({ block: 'nearest' });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [currentMatchWordId, speakerParts]);

  const handleChangeSpeaker = (
    segmentsIds: string[],
    menuItem: MenuItem,
  ): void => {
    if (menuItem.name === 'manage') {
      openModal({ id: TRANSCRIPTIONS_EDIT_SPEAKERS });
    } else if (menuItem.name) {
      const [, speakerId] = menuItem.name.split('_');
      if (speakerId) {
        openModal({
          id: TRANSCRIPTIONS_CHANGE_SEGMENTS_SPEAKER,
          contextItems: [
            {
              id: speakerId,
              segmentsIds,
            },
          ],
        });
      }
    }
  };

  const listElements = speakerParts.map((part, index) => {
    const speakerName = !isNil(part.speaker)
      ? speakerLabels[part.speaker]
      : 'Speaker not set';

    const language = transcriptionProperties?.language ?? null;

    const shouldAddSpace =
      !language || !LANGUAGES_WITHOUT_SPACE.includes(language);

    const segmentByIds = at(segments, part.ids);

    return (
      <div className={styles.part} key={`part_${index}`}>
        <div
          className={classNames(
            styles.speaker,
            !isNil(part.speaker)
              ? speakerColorClasses[part.speaker]
              : undefined,
          )}
        >
          {canEdit ? (
            <TriggerMenu
              triggerElement={<SpeakerButtonMenu name={speakerName} />}
              hDirectionHint="left"
              menuItems={selectSpeakerMenuItems(speakerLabels, part.speaker)}
              onMenuItemClick={(menuItem) =>
                handleChangeSpeaker(part.ids, menuItem)
              }
              showIcons
            />
          ) : (
            <button
              className={classNames(styles.speaker_button, mixins.reset_button)}
            >
              <span className={styles.speaker_label}>{speakerName}</span>
            </button>
          )}
        </div>
        <div className={styles.text}>
          {segmentByIds.map((segment) => (
            <React.Fragment key={segment.id}>
              <div className={styles.segment} id={`segment-${segment.id}`}>
                {segment.transcription?.words.map((word, wordIndex) => {
                  const wordId = `w_${segment.id}_${wordIndex}`;
                  return (
                    <span
                      key={wordId}
                      id={wordId}
                      className={classNames({
                        [styles.low_score]: word.score && word.score < 0.75,
                        [styles.selected]: selectedWordIdsSet.has(wordId),
                        [styles.highlighted]:
                          highlightedTextWordIdsSet.has(wordId),
                        [styles.marked]: markedWordIdsSet.has(wordId),
                        [styles.current_marked]: currentMatchWordId === wordId,
                      })}
                    >
                      {word.value}
                      {shouldAddSpace ? ' ' : ''}
                    </span>
                  );
                })}
              </div>
              {shouldAddSpace ? ' ' : ''}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  });

  return <VList ref={vListRef}>{listElements}</VList>;
};
